/**
 * HTTP Client
 * Centralized API client with interceptors, retry logic, and error handling
 */

import type {
  RequestConfig,
  ApiResponse,
} from './types';
import { ApiError } from './errors';
import {
  API_CONFIG,
  getEndpointUrl,
  type ApiEndpoint,
} from './config';
import {
  InterceptorManager,
  defaultRequestInterceptor,
  defaultResponseInterceptor,
  defaultErrorInterceptor,
} from './interceptors';

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if error is retryable
 */
function isRetryableError(error: ApiError): boolean {
  // Retry on network errors, timeouts, and 5xx server errors
  return (
    error.isNetworkError() ||
    error.isTimeout() ||
    (error.status !== undefined && error.status >= 500)
  );
}

/**
 * HTTP Client Class
 */
export class ApiClient {
  private interceptors: InterceptorManager;

  constructor() {
    this.interceptors = new InterceptorManager();
    
    // Add default interceptors
    this.interceptors.addRequestInterceptor(defaultRequestInterceptor);
    this.interceptors.addResponseInterceptor(defaultResponseInterceptor);
    this.interceptors.addErrorInterceptor(defaultErrorInterceptor);
  }

  /**
   * Get interceptors manager
   */
  getInterceptors(): InterceptorManager {
    return this.interceptors;
  }

  /**
   * Make HTTP request with retry logic
   */
  async request<T = any>(
    endpoint: ApiEndpoint,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = API_CONFIG.TIMEOUT,
      signal,
      retry = true,
      retryAttempts = API_CONFIG.RETRY_ATTEMPTS,
    } = config;

    const url = getEndpointUrl(endpoint);

    // Prepare request config
    let requestConfig: RequestConfig = {
      method,
      headers,
      body,
      timeout,
      signal,
    };

    // Execute request interceptors
    requestConfig = await this.interceptors.executeRequestInterceptors(requestConfig);

    // Attempt request with retry logic
    let lastError: ApiError | null = null;
    let attempt = 0;

    while (attempt <= retryAttempts) {
      try {
        const response = await this.executeRequest(url, requestConfig);
        
        // Execute response interceptors
        const processedResponse = await this.interceptors.executeResponseInterceptors(response);

        // Handle error responses
        if (!processedResponse.ok) {
          const errorData = await this.parseErrorResponse(processedResponse);
          throw ApiError.fromResponse(processedResponse, errorData);
        }

        // Parse successful response
        const data = await this.parseResponse<T>(processedResponse);

        return {
          data,
          status: processedResponse.status,
          statusText: processedResponse.statusText,
          headers: processedResponse.headers,
        };
      } catch (error) {
        lastError = this.handleRequestError(error);

        // Check if we should retry
        if (
          !retry ||
          attempt >= retryAttempts ||
          !isRetryableError(lastError) ||
          signal?.aborted
        ) {
          break;
        }

        // Wait before retrying (exponential backoff)
        const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
        await sleep(delay);
        attempt++;
      }
    }

    // Execute error interceptors
    const processedError = await this.interceptors.executeErrorInterceptors(
      lastError || ApiError.fromNetworkError(new Error('Unknown error'))
    );

    throw processedError;
  }

  /**
   * Execute the actual HTTP request
   */
  private async executeRequest(
    url: string,
    config: RequestConfig
  ): Promise<Response> {
    const { method = 'GET', headers = {}, body, timeout, signal } = config;

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = timeout
      ? setTimeout(() => controller.abort(), timeout)
      : null;

    // Combine abort signals
    const abortSignal = signal
      ? (() => {
          const combinedController = new AbortController();
          signal.addEventListener('abort', () => combinedController.abort());
          controller.signal.addEventListener('abort', () => combinedController.abort());
          return combinedController.signal;
        })()
      : controller.signal;

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: abortSignal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      return response;
    } catch (error: any) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (error.name === 'AbortError') {
        if (controller.signal.aborted) {
          throw ApiError.fromTimeout();
        }
        throw ApiError.fromAbort();
      }

      throw error;
    }
  }

  /**
   * Parse successful response
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    
    if (contentType?.includes('application/json')) {
      return await response.json();
    }
    
    if (contentType?.includes('text/')) {
      return (await response.text()) as unknown as T;
    }

    return (await response.blob()) as unknown as T;
  }

  /**
   * Parse error response
   */
  private async parseErrorResponse(response: Response): Promise<any> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      return { message: await response.text() };
    } catch {
      return {};
    }
  }

  /**
   * Handle request errors
   */
  private handleRequestError(error: any): ApiError {
    if (error instanceof ApiError) {
      return error;
    }

    // Check for CORS or network errors
    if (error instanceof TypeError) {
      const errorMessage = error.message || '';
      // CORS errors often manifest as "Failed to fetch" or network errors
      if (errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
        return ApiError.fromNetworkError(error);
      }
    }

    // Check for CORS-related error messages
    if (error instanceof Error) {
      const errorMessage = error.message || error.toString();
      if (
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control') ||
        errorMessage.includes('blocked by CORS policy')
      ) {
        return ApiError.fromNetworkError(error);
      }
    }

    return ApiError.fromNetworkError(
      error instanceof Error ? error : new Error('Unknown error')
    );
  }

  /**
   * Convenience methods for HTTP verbs
   */
  async get<T = any>(endpoint: ApiEndpoint, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T = any>(endpoint: ApiEndpoint, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  async put<T = any>(endpoint: ApiEndpoint, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  async patch<T = any>(endpoint: ApiEndpoint, body?: any, config?: Omit<RequestConfig, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  async delete<T = any>(endpoint: ApiEndpoint, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export for custom instances if needed
export default ApiClient;


